import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Speed as EfficiencyIcon,
  Timeline as TimelineIcon,
  Assessment as PerformanceIcon,
  CheckCircle as QualityIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import api from '../services/api';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface OEEData {
  machine: string;
  running_minutes: number;
  loss_minutes: number;
  planned_production_time: number;
  availability: number;
  actual_output: number;
  theoretical_output: number;
  performance: number;
  oee: number;
  total_jobs: number;
  running_jobs: number;
  loss_jobs: number;
}

interface MonthlyData {
  date: string;
  running_minutes: number;
  loss_minutes: number;
  availability: number;
  performance: number;
  oee: number;
}

const machines = [
  'R1',
  'R4',
  'R5',
  'Steamer 01',
  'Steamer 02',
  '48-Digital',
  'MS-Digital',
  'Sublimation',
  'Washer',
];

const OEEDashboard: React.FC = () => {
  const [selectedMachine, setSelectedMachine] = useState<string>('R1');
  const [selectedMonth, setSelectedMonth] = useState<Date>(new Date());
  const [oeeData, setOeeData] = useState<OEEData | null>(null);
  const [monthlyTrend, setMonthlyTrend] = useState<MonthlyData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to safely format numbers
  const safeToFixed = (value: number | null | undefined, decimals: number = 2): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0.' + '0'.repeat(decimals);
    }
    return value.toFixed(decimals);
  };

  useEffect(() => {
    fetchOEEData();
  }, [selectedMachine, selectedMonth]);

  const fetchOEEData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const startDate = format(startOfMonth(selectedMonth), 'yyyy-MM-dd');
      const endDate = format(endOfMonth(selectedMonth), 'yyyy-MM-dd');

      // Fetch OEE data for the selected machine and month
      const response = await api.get('/machine-efficiency/oee', {
        params: {
          machine: selectedMachine,
          start_date: startDate,
          end_date: endDate,
        },
      });

      setOeeData(response.data);

      // Fetch daily trend for the month
      const trendResponse = await api.get('/machine-efficiency/oee-trend', {
        params: {
          machine: selectedMachine,
          start_date: startDate,
          end_date: endDate,
        },
      });

      // Deduplicate by date (in case backend returns duplicates)
      const dateMap = new Map<string, MonthlyData>();
      trendResponse.data.forEach((item: MonthlyData) => {
        // Normalize date to YYYY-MM-DD format
        const dateStr = typeof item.date === 'string' 
          ? item.date.split('T')[0] 
          : new Date(item.date).toISOString().split('T')[0];
        
        // Only keep the first entry for each date, or merge if needed
        if (!dateMap.has(dateStr)) {
          dateMap.set(dateStr, { ...item, date: dateStr });
        }
      });
      
      // Convert map back to array and sort by date
      const deduplicatedTrend = Array.from(dateMap.values()).sort((a, b) => 
        a.date.localeCompare(b.date)
      );

      setMonthlyTrend(deduplicatedTrend);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch OEE data');
      console.error('Fetch OEE data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getOEEColor = (oee: number): string => {
    if (oee >= 85) return '#4CAF50'; // Excellent - Green
    if (oee >= 70) return '#8BC34A'; // Good - Light Green
    if (oee >= 60) return '#FFC107'; // Fair - Yellow
    if (oee >= 40) return '#FF9800'; // Poor - Orange
    return '#F44336'; // Very Poor - Red
  };

  const getAvailabilityColor = (availability: number): string => {
    if (availability >= 90) return '#4CAF50';
    if (availability >= 80) return '#8BC34A';
    if (availability >= 70) return '#FFC107';
    return '#FF9800';
  };

  const getPerformanceColor = (performance: number): string => {
    if (performance >= 95) return '#4CAF50';
    if (performance >= 85) return '#8BC34A';
    if (performance >= 75) return '#FFC107';
    return '#FF9800';
  };

  // Time Distribution Pie Chart
  const timeDistributionData = oeeData ? {
    labels: ['Running Time', 'Loss Time'],
    datasets: [
      {
        data: [
          oeeData.running_minutes || 0,
          oeeData.loss_minutes || 0,
        ],
        backgroundColor: ['#4CAF50', '#F44336'],
        borderWidth: 2,
        borderColor: '#fff',
      },
    ],
  } : null;

  // Job Distribution Bar Chart
  const jobDistributionData = oeeData ? {
    labels: ['Running Jobs', 'Loss Jobs'],
    datasets: [
      {
        label: 'Number of Jobs',
        data: [oeeData.running_jobs || 0, oeeData.loss_jobs || 0],
        backgroundColor: ['#2196F3', '#FF5722'],
        borderWidth: 1,
        borderColor: '#fff',
      },
    ],
  } : null;

  // Monthly Trend Line Chart
  const trendData = monthlyTrend.length > 0 ? {
    labels: monthlyTrend.map(d => {
      // Parse date as YYYY-MM-DD format to avoid timezone issues
      const [year, month, day] = d.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd');
    }),
    datasets: [
      {
        label: 'OEE %',
        data: monthlyTrend.map(d => Math.round((d.oee || 0) * 100) / 100),
        borderColor: '#673AB7',
        backgroundColor: 'rgba(103, 58, 183, 0.1)',
        tension: 0,
        fill: true,
      },
      {
        label: 'Availability %',
        data: monthlyTrend.map(d => Math.round((d.availability || 0) * 100) / 100),
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        tension: 0,
        fill: true,
      },
      {
        label: 'Performance %',
        data: monthlyTrend.map(d => Math.round((d.performance || 0) * 100) / 100),
        borderColor: '#FF9800',
        backgroundColor: 'rgba(255, 152, 0, 0.1)',
        tension: 0,
        fill: true,
      },
    ],
  } : null;

  // Time Distribution Bar Chart - shows running vs loss minutes stacked
  const timeBarData = monthlyTrend.length > 0 ? {
    labels: monthlyTrend.map(d => {
      // Parse date as YYYY-MM-DD format to avoid timezone issues
      const [year, month, day] = d.date.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return format(date, 'MMM dd');
    }),
    datasets: [
      {
        label: 'Running Minutes',
        data: monthlyTrend.map(d => Math.round(d.running_minutes || 0)),
        backgroundColor: '#4CAF50',
        borderColor: '#388E3C',
        borderWidth: 1,
      },
      {
        label: 'Loss Minutes',
        data: monthlyTrend.map(d => Math.round(d.loss_minutes || 0)),
        backgroundColor: '#F44336',
        borderColor: '#D32F2F',
        borderWidth: 1,
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom' as const,
      },
    },
  };

  const lineChartOptions = {
    ...chartOptions,
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          callback: function(value: any) {
            return Math.round(value) + '%';
          }
        }
      },
    },
    plugins: {
      ...chartOptions.plugins,
      tooltip: {
        callbacks: {
          label: function(context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.parsed.y !== null) {
              label += Math.round(context.parsed.y * 100) / 100 + '%';
            }
            return label;
          }
        }
      }
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #00695C 0%, #004d40 100%)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EfficiencyIcon sx={{ fontSize: 40, color: 'white' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'white' }}>
                OEE Dashboard
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                Overall Equipment Efficiency Analysis - 24/7 Operations
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                fullWidth
                label="Select Machine"
                value={selectedMachine}
                onChange={(e) => setSelectedMachine(e.target.value)}
                size="small"
              >
                {machines.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Select Month"
                views={['year', 'month']}
                value={selectedMonth}
                onChange={(newValue) => {
                  if (newValue) {
                    setSelectedMonth(newValue as Date);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchOEEData}
                fullWidth
                disabled={loading}
              >
                Refresh Data
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : oeeData ? (
          <>
            {/* KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* OEE Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #00695C 0%, #004d40 100%)' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EfficiencyIcon sx={{ color: 'white', mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
                        Overall OEE
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                      {safeToFixed(oeeData.oee, 2)}%
                    </Typography>
                    <Chip
                      label={
                        (oeeData.oee || 0) >= 85 ? 'Excellent' :
                        (oeeData.oee || 0) >= 70 ? 'Good' :
                        (oeeData.oee || 0) >= 60 ? 'Fair' :
                        (oeeData.oee || 0) >= 40 ? 'Poor' : 'Very Poor'
                      }
                      size="small"
                      sx={{ backgroundColor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
                    />
                  </CardContent>
                </Card>
              </Grid>

              {/* Availability Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <TimelineIcon sx={{ color: getAvailabilityColor(oeeData.availability || 0), mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Availability
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: getAvailabilityColor(oeeData.availability || 0), mb: 1 }}>
                      {safeToFixed(oeeData.availability, 2)}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Running: {safeToFixed(oeeData.running_minutes, 0)} min
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Performance Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PerformanceIcon sx={{ color: getPerformanceColor(oeeData.performance || 0), mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Performance
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: getPerformanceColor(oeeData.performance || 0), mb: 1 }}>
                      {safeToFixed(oeeData.performance, 2)}%
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Output: {safeToFixed(oeeData.actual_output, 0)} units
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              {/* Total Jobs Card */}
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <QualityIcon sx={{ color: '#2196F3', mr: 1 }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Total Jobs
                      </Typography>
                    </Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, color: '#2196F3', mb: 1 }}>
                      {oeeData.total_jobs || 0}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Running: {oeeData.running_jobs || 0} | Loss: {oeeData.loss_jobs || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Time Breakdown */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Time Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                      Running Time (Productive)
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#4CAF50', fontWeight: 600 }}>
                      {safeToFixed(oeeData.running_minutes, 0)} min
                    </Typography>
                    <Typography variant="caption">
                      ({safeToFixed((oeeData.running_minutes || 0) / 60, 1)} hours)
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Stack spacing={1}>
                    <Typography variant="body2" color="textSecondary">
                      Loss Time (Includes All Downtime)
                    </Typography>
                    <Typography variant="h6" sx={{ color: '#F44336', fontWeight: 600 }}>
                      {safeToFixed(oeeData.loss_minutes, 0)} min
                    </Typography>
                    <Typography variant="caption">
                      ({safeToFixed((oeeData.loss_minutes || 0) / 60, 1)} hours)
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>

            {/* Charts Row 1 */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Time Distribution Pie Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Time Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {timeDistributionData && <Pie data={timeDistributionData} options={chartOptions} />}
                  </Box>
                </Paper>
              </Grid>

              {/* Job Distribution Bar Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Job Distribution
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    {jobDistributionData && <Bar data={jobDistributionData} options={chartOptions} />}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Charts Row 2 */}
            <Grid container spacing={3}>
              {/* OEE Trend Line Chart */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Monthly OEE Trend
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    {trendData && <Line data={trendData} options={lineChartOptions} />}
                  </Box>
                </Paper>
              </Grid>

              {/* Time Distribution Bar Chart */}
              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Daily Time Distribution (Minutes per Day)
                  </Typography>
                  <Box sx={{ height: 350 }}>
                    {timeBarData && (
                      <Bar
                        data={timeBarData}
                        options={{
                          ...chartOptions,
                          scales: {
                            x: {
                              stacked: true,
                            },
                            y: {
                              stacked: true,
                              beginAtZero: true,
                              max: 1440,
                              ticks: {
                                callback: function(value: any) {
                                  return Math.round(value) + ' min';
                                }
                              },
                              title: {
                                display: true,
                                text: 'Minutes (Total = 1440 min/day)'
                              }
                            },
                          },
                          plugins: {
                            ...chartOptions.plugins,
                            tooltip: {
                              callbacks: {
                                label: function(context: any) {
                                  let label = context.dataset.label || '';
                                  if (label) {
                                    label += ': ';
                                  }
                                  if (context.parsed.y !== null) {
                                    const minutes = Math.round(context.parsed.y);
                                    const hours = (minutes / 60).toFixed(1);
                                    label += `${minutes} min (${hours} hrs)`;
                                  }
                                  return label;
                                },
                                footer: function(tooltipItems: any) {
                                  let total = 0;
                                  tooltipItems.forEach((item: any) => {
                                    total += item.parsed.y;
                                  });
                                  return 'Total: ' + Math.round(total) + ' min (' + (total / 60).toFixed(1) + ' hrs)';
                                }
                              }
                            }
                          },
                        }}
                      />
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>

            {/* Formula Reference */}
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                OEE Calculation Formulas (24/7 Operation)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Availability = (Running Time / Planned Production Time) × 100%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Running Time = Sum of all running jobs (minutes)
                      <br />
                      • Planned Production Time = 24h × 60min = 1,440 minutes/day
                      <br />
                      • <strong>Loss Time = All non-running time (includes scheduled losses, unscheduled downtime, and idle time)</strong>
                      <br />
                      • <strong>Machines operate 24/7 continuously</strong>
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                      Performance = (Actual Output / Theoretical Output) × 100%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • Actual Output = Quantity produced
                      <br />
                      • Theoretical Output = Machine Speed × Running Time
                      <br />
                      • <strong>Quality assumed at 100%</strong>
                      <br />
                      • <strong>OEE = Availability × Performance</strong>
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </>
        ) : (
          <Paper sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="h6" color="textSecondary">
              No data available for the selected period
            </Typography>
          </Paper>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default OEEDashboard;
